import { useMemo, useState } from "react";
import {
    FiEdit3,
    FiEye,
    FiSearch,
    FiSend,
    FiSettings,
    FiUserX,
} from "react-icons/fi";

import { supabase } from "../../../config/supabase";
import DropdownSection from "../../common/DropdownSection";

const PRICING_FIELD_OPTIONS = [
    { key: "roof", label: "Roof Trusses", group: "Roof" },
    { key: "labour", label: "Labour", group: "Roof" },
    { key: "first_floor", label: "1st Floor Trusses", group: "Floor" },
    { key: "second_floor", label: "2nd Floor Trusses", group: "Floor" },
    { key: "gf_wall", label: "Ground Floor Walls", group: "Wall" },
    { key: "first_floor_wall", label: "1st Floor Walls", group: "Wall" },
    { key: "second_floor_wall", label: "2nd Floor Walls", group: "Wall" },
    { key: "beams", label: "Beams", group: "Additional" },
    { key: "flooring_sheets", label: "Flooring Sheets", group: "Additional" },
];

const DEFAULT_ENABLED_PRICING_FIELDS = PRICING_FIELD_OPTIONS.map(f => f.key);

const FINAL_PRICING_VIEW_PERMISSION = "view_finalpricing_page";
const FINAL_PRICING_EDIT_PERMISSION = "edit_finalpricing_page";

const FINAL_PRICING_ACCESS_OPTIONS = [
    {
        value: "none",
        label: "No Access",
        description: "Cannot view or edit final pricing.",
        icon: FiUserX,
    },
    {
        value: "view",
        label: "View Only",
        description: "Can view final pricing but cannot make changes.",
        icon: FiEye,
    },
    {
        value: "edit",
        label: "View & Edit",
        description: "Can view and update final pricing.",
        icon: FiEdit3,
    },
];

const QUOTATION_VIEW_PERMISSION = "view_quotation_page";
const QUOTATION_SEND_PERMISSION = "send_quotation_page";

const QUOTATION_ACCESS_OPTIONS = [
    {
        value: "none",
        label: "No Access",
        description: "Cannot view or send quotations.",
        icon: FiUserX,
    },
    {
        value: "view",
        label: "View Only",
        description: "Can view priced jobs but cannot send quotation emails.",
        icon: FiEye,
    },
    {
        value: "send",
        label: "View & Send",
        description: "Can view priced jobs and email quotations to customers.",
        icon: FiSend,
    },
];

const SectionCard = ({ title, description, icon, children, right }) => (
    <section className="settings-section rounded-3xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-start gap-3">
                {icon && (
                    <div className="w-10 h-10 rounded-2xl bg-zinc-100 text-zinc-700 flex items-center justify-center shrink-0">
                        {icon}
                    </div>
                )}

                <div>
                    <h2 className="text-lg font-semibold text-zinc-900">
                        {title}
                    </h2>
                    {description && (
                        <p className="text-sm text-gray-500 mt-1 max-w-3xl">
                            {description}
                        </p>
                    )}
                </div>
            </div>

            {right}
        </div>

        <div className="p-5">
            {children}
        </div>
    </section>
);

const TogglePill = ({ selected, disabled, onClick, children, tone = "zinc" }) => {
    const active = {
        zinc: "border-zinc-900 bg-zinc-900 text-white",
        sky: "border-sky-600 bg-sky-600 text-white",
        green: "border-green-600 bg-green-600 text-white",
    };

    return (
        <button
            type="button"
            disabled={disabled}
            onClick={onClick}
            className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition disabled:opacity-60 disabled:cursor-not-allowed ${selected
                ? active[tone]
                : "border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50"
                }`}
        >
            {children}
        </button>
    );
};

const defaultPricingSettings = {
    send_email_on_estimation_complete: false,
    estimation_complete_email_user_ids: [],
    allow_estimators_finish_estimation_without_pricing: true,
    allow_designers_finish_estimation_without_pricing: true,
    enabled_pricing_fields: DEFAULT_ENABLED_PRICING_FIELDS,
};

const PricingQuotationSettings = ({
    company,
    userProfile,
    users,
    setUsers,
    updateCompanySettings,
    savingCompanySettings,
    isSuperAdmin,
    setError,
}) => {
    const [pricingSettings, setPricingSettings] = useState({
        ...defaultPricingSettings,
        ...(company?.settings?.pricing_settings || {}),
    });

    const [finalPricingUserSearch, setFinalPricingUserSearch] = useState("");
    const [savingFinalPricingUserIds, setSavingFinalPricingUserIds] = useState([]);
    const [quotationUserSearch, setQuotationUserSearch] = useState("");
    const [savingQuotationUserIds, setSavingQuotationUserIds] = useState([]);

    const companyUsers = useMemo(() => users || [], [users]);

    const selectedPricingFields =
        pricingSettings.enabled_pricing_fields || DEFAULT_ENABLED_PRICING_FIELDS;

    const finalPricingRequired =
        typeof pricingSettings.final_pricing_required_for_quotation === "boolean"
            ? pricingSettings.final_pricing_required_for_quotation
            : (pricingSettings.final_pricing_mode || "entered_final_pricing") === "entered_final_pricing";

    const updatePricingSettings = async (patch) => {
        const next = {
            ...pricingSettings,
            ...patch,
        };

        setPricingSettings(next);

        await updateCompanySettings(
            { pricing_settings: next },
            "Pricing settings updated"
        );
    };

    const togglePricingField = async (fieldKey) => {
        if (!isSuperAdmin) return;

        const current =
            pricingSettings.enabled_pricing_fields || DEFAULT_ENABLED_PRICING_FIELDS;

        const nextFields = current.includes(fieldKey)
            ? current.filter(key => key !== fieldKey)
            : [...current, fieldKey];

        await updatePricingSettings({
            enabled_pricing_fields: nextFields,
        });
    };

    const togglePricingEmailUser = async (userId) => {
        const current = pricingSettings.estimation_complete_email_user_ids || [];
        const nextUsers = current.includes(userId)
            ? current.filter(id => id !== userId)
            : [...current, userId];

        await updatePricingSettings({
            estimation_complete_email_user_ids: nextUsers,
        });
    };

    const setFinalPricingRequired = (enabled) => {
        updatePricingSettings({ final_pricing_required_for_quotation: enabled });
    };

    const setPricingEnteredNotifyEnabled = (enabled) => {
        updatePricingSettings({ send_email_on_estimation_complete: enabled });
    };

    const setReadyForQuotationNotifyEnabled = (enabled) => {
        updatePricingSettings({ notify_final_pricing_entered: enabled });
    };

    const toggleReadyForQuotationNotifyUser = async (userId) => {
        const current = pricingSettings.final_pricing_entered_notify_user_ids || [];
        const nextUsers = current.includes(userId)
            ? current.filter(id => id !== userId)
            : [...current, userId];

        await updatePricingSettings({
            final_pricing_entered_notify_user_ids: nextUsers,
        });
    };

    const getFinalPricingAccessLevel = (user) => {
        const userPermissions = Array.isArray(user?.permissions)
            ? user.permissions
            : [];

        const canView = userPermissions.includes(
            FINAL_PRICING_VIEW_PERMISSION
        );

        const canEdit = userPermissions.includes(
            FINAL_PRICING_EDIT_PERMISSION
        );

        if (canEdit) return "edit";
        if (canView) return "view";

        return "none";
    };

    const getFinalPricingPermissionsForAccess = (
        currentPermissions,
        accessLevel
    ) => {
        const permissions = Array.isArray(currentPermissions)
            ? currentPermissions
            : [];

        const unrelatedPermissions = permissions.filter(
            permission =>
                permission !== FINAL_PRICING_VIEW_PERMISSION &&
                permission !== FINAL_PRICING_EDIT_PERMISSION
        );

        if (accessLevel === "edit") {
            return [
                ...unrelatedPermissions,
                FINAL_PRICING_VIEW_PERMISSION,
                FINAL_PRICING_EDIT_PERMISSION,
            ];
        }

        if (accessLevel === "view") {
            return [
                ...unrelatedPermissions,
                FINAL_PRICING_VIEW_PERMISSION,
            ];
        }

        return unrelatedPermissions;
    };

    const updateFinalPricingUserAccess = async (
        targetUser,
        accessLevel
    ) => {
        if (!isSuperAdmin || !targetUser?.id) return;

        const previousPermissions = Array.isArray(targetUser.permissions)
            ? targetUser.permissions
            : [];

        const nextPermissions = getFinalPricingPermissionsForAccess(
            previousPermissions,
            accessLevel
        );

        const previousAccessLevel =
            getFinalPricingAccessLevel(targetUser);

        if (previousAccessLevel === accessLevel) return;

        setSavingFinalPricingUserIds(previous => [
            ...previous,
            targetUser.id,
        ]);

        // Optimistic update
        setUsers(previous =>
            previous.map(user =>
                user.id === targetUser.id
                    ? {
                        ...user,
                        permissions: nextPermissions,
                    }
                    : user
            )
        );

        try {
            const { error: updateError } = await supabase
                .from("users")
                .update({
                    permissions: nextPermissions,
                })
                .eq("id", targetUser.id)
                .eq("company_id", company.id);

            if (updateError) throw updateError;

            const targetUserName =
                targetUser.full_name ||
                targetUser.username ||
                targetUser.email ||
                "Unknown user";

            const accessLabel =
                FINAL_PRICING_ACCESS_OPTIONS.find(
                    option => option.value === accessLevel
                )?.label || accessLevel;

            const { error: logError } = await supabase
                .from("job_action_logs")
                .insert({
                    job_id: null,
                    company_id: company.id,
                    user_id: userProfile?.id || null,
                    user_name:
                        userProfile?.full_name ||
                        userProfile?.username ||
                        userProfile?.email ||
                        "Unknown",
                    user_role: userProfile?.role || null,
                    action: "user_permissions_updated",
                    details:
                        `${targetUserName}'s final pricing access changed ` +
                        `to ${accessLabel}`,
                    changed_fields: ["permissions"],
                    old_values: {
                        user_id: targetUser.id,
                        permissions: previousPermissions,
                        final_pricing_access: previousAccessLevel,
                    },
                    new_values: {
                        user_id: targetUser.id,
                        permissions: nextPermissions,
                        final_pricing_access: accessLevel,
                    },
                    action_origin: "settings_page",
                });

            if (logError) {
                console.error(
                    "Failed to create final pricing permission log:",
                    logError
                );
            }
        } catch (err) {
            console.error(
                "Failed to update final pricing access:",
                err
            );

            // Roll back optimistic change
            setUsers(previous =>
                previous.map(user =>
                    user.id === targetUser.id
                        ? {
                            ...user,
                            permissions: previousPermissions,
                        }
                        : user
                )
            );

            setError(
                err.message ||
                "Failed to update final pricing permissions"
            );
        } finally {
            setSavingFinalPricingUserIds(previous =>
                previous.filter(id => id !== targetUser.id)
            );
        }
    };

    const getQuotationAccessLevel = (user) => {
        const userPermissions = Array.isArray(user?.permissions)
            ? user.permissions
            : [];

        const canView = userPermissions.includes(QUOTATION_VIEW_PERMISSION);
        const canSend = userPermissions.includes(QUOTATION_SEND_PERMISSION);

        if (canSend) return "send";
        if (canView) return "view";

        return "none";
    };

    const getQuotationPermissionsForAccess = (
        currentPermissions,
        accessLevel
    ) => {
        const permissions = Array.isArray(currentPermissions)
            ? currentPermissions
            : [];

        const unrelatedPermissions = permissions.filter(
            permission =>
                permission !== QUOTATION_VIEW_PERMISSION &&
                permission !== QUOTATION_SEND_PERMISSION
        );

        if (accessLevel === "send") {
            return [
                ...unrelatedPermissions,
                QUOTATION_VIEW_PERMISSION,
                QUOTATION_SEND_PERMISSION,
            ];
        }

        if (accessLevel === "view") {
            return [
                ...unrelatedPermissions,
                QUOTATION_VIEW_PERMISSION,
            ];
        }

        return unrelatedPermissions;
    };

    const updateQuotationUserAccess = async (
        targetUser,
        accessLevel
    ) => {
        if (!isSuperAdmin || !targetUser?.id) return;

        const previousPermissions = Array.isArray(targetUser.permissions)
            ? targetUser.permissions
            : [];

        const nextPermissions = getQuotationPermissionsForAccess(
            previousPermissions,
            accessLevel
        );

        const previousAccessLevel = getQuotationAccessLevel(targetUser);

        if (previousAccessLevel === accessLevel) return;

        setSavingQuotationUserIds(previous => [...previous, targetUser.id]);

        setUsers(previous =>
            previous.map(user =>
                user.id === targetUser.id
                    ? { ...user, permissions: nextPermissions }
                    : user
            )
        );

        try {
            const { error: updateError } = await supabase
                .from("users")
                .update({ permissions: nextPermissions })
                .eq("id", targetUser.id)
                .eq("company_id", company.id);

            if (updateError) throw updateError;

            const targetUserName =
                targetUser.full_name ||
                targetUser.username ||
                targetUser.email ||
                "Unknown user";

            const accessLabel =
                QUOTATION_ACCESS_OPTIONS.find(
                    option => option.value === accessLevel
                )?.label || accessLevel;

            const { error: logError } = await supabase
                .from("job_action_logs")
                .insert({
                    job_id: null,
                    company_id: company.id,
                    user_id: userProfile?.id || null,
                    user_name:
                        userProfile?.full_name ||
                        userProfile?.username ||
                        userProfile?.email ||
                        "Unknown",
                    user_role: userProfile?.role || null,
                    action: "user_permissions_updated",
                    details:
                        `${targetUserName}'s quotation access changed ` +
                        `to ${accessLabel}`,
                    changed_fields: ["permissions"],
                    old_values: {
                        user_id: targetUser.id,
                        permissions: previousPermissions,
                        quotation_access: previousAccessLevel,
                    },
                    new_values: {
                        user_id: targetUser.id,
                        permissions: nextPermissions,
                        quotation_access: accessLevel,
                    },
                    action_origin: "settings_page",
                });

            if (logError) {
                console.error(
                    "Failed to create quotation permission log:",
                    logError
                );
            }
        } catch (err) {
            console.error("Failed to update quotation access:", err);

            setUsers(previous =>
                previous.map(user =>
                    user.id === targetUser.id
                        ? { ...user, permissions: previousPermissions }
                        : user
                )
            );

            setError(err.message || "Failed to update quotation permissions");
        } finally {
            setSavingQuotationUserIds(previous =>
                previous.filter(id => id !== targetUser.id)
            );
        }
    };

    const filteredFinalPricingUsers = useMemo(() => {
        const query = finalPricingUserSearch
            .trim()
            .toLowerCase();

        if (!query) return companyUsers;

        return companyUsers.filter(user => {
            const searchableValues = [
                user.full_name,
                user.username,
                user.email,
                user.role,
                user.position,
            ];

            return searchableValues.some(value =>
                String(value || "")
                    .toLowerCase()
                    .includes(query)
            );
        });
    }, [companyUsers, finalPricingUserSearch]);

    const finalPricingAccessCounts = useMemo(() => {
        return companyUsers.reduce(
            (counts, user) => {
                const accessLevel =
                    getFinalPricingAccessLevel(user);

                counts[accessLevel] += 1;

                return counts;
            },
            {
                none: 0,
                view: 0,
                edit: 0,
            }
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyUsers]);

    const filteredQuotationUsers = useMemo(() => {
        const query = quotationUserSearch.trim().toLowerCase();

        if (!query) return companyUsers;

        return companyUsers.filter(user => {
            const searchableValues = [
                user.full_name,
                user.username,
                user.email,
                user.role,
                user.position,
            ];

            return searchableValues.some(value =>
                String(value || "").toLowerCase().includes(query)
            );
        });
    }, [companyUsers, quotationUserSearch]);

    const quotationAccessCounts = useMemo(() => {
        return companyUsers.reduce(
            (counts, user) => {
                const accessLevel = getQuotationAccessLevel(user);
                counts[accessLevel] += 1;
                return counts;
            },
            {
                none: 0,
                view: 0,
                send: 0,
            }
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyUsers]);

    return (
        <SectionCard
            title="Pricing & Quotation"
            description="Set pricing completion behaviour, final pricing mode, pricing/quotation access, and notification recipients."
            icon={<FiSettings />}
        >
            <div className="space-y-5">
                <div className="rounded-2xl bg-gray-50 border border-gray-100 p-4 space-y-3">
                    <label className="flex items-start gap-3 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={pricingSettings.allow_estimators_finish_estimation_without_pricing !== false}
                            onChange={(e) =>
                                updatePricingSettings({
                                    allow_estimators_finish_estimation_without_pricing: e.target.checked,
                                })
                            }
                            className="mt-1"
                        />
                        <div>
                            <p className="text-sm font-semibold text-zinc-800">
                                Estimators can finish without pricing
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                                If disabled, estimators must enter pricing first.
                            </p>
                        </div>
                    </label>

                    <label className="flex items-start gap-3 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={pricingSettings.allow_designers_finish_estimation_without_pricing !== false}
                            onChange={(e) =>
                                updatePricingSettings({
                                    allow_designers_finish_estimation_without_pricing: e.target.checked,
                                })
                            }
                            className="mt-1"
                        />
                        <div>
                            <p className="text-sm font-semibold text-zinc-800">
                                Designers can finish without pricing
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                                If disabled, designers must enter department pricing first.
                            </p>
                        </div>
                    </label>
                </div>

                <DropdownSection
                    title="Pricing inputs"
                    subtext={`${selectedPricingFields.length} enabled`}
                    defaultOpen={false}
                    titleStyle="text-sm font-semibold"
                    subtextStyle="text-xs text-gray-500"
                    customMainStyle="!shadow-none !border-gray-100"
                    customClickableStyle="!px-4 !py-3"
                    customContainerStyle="!p-4 !bg-gray-50"
                >
                    <div className="space-y-4">
                        {["Roof", "Floor", "Wall", "Additional"].map(group => (
                            <div key={group} className="rounded-2xl bg-white border border-gray-100 p-4">
                                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
                                    {group}
                                </p>

                                <div className="flex flex-wrap gap-2">
                                    {PRICING_FIELD_OPTIONS
                                        .filter(field => field.group === group)
                                        .map(field => {
                                            const enabled = selectedPricingFields.includes(field.key);

                                            return (
                                                <TogglePill
                                                    key={field.key}
                                                    selected={enabled}
                                                    disabled={savingCompanySettings}
                                                    onClick={() => togglePricingField(field.key)}
                                                >
                                                    {enabled ? "On" : "Off"} · {field.label}
                                                </TogglePill>
                                            );
                                        })}
                                </div>
                            </div>
                        ))}
                    </div>

                    <p className="text-xs text-gray-400 mt-3">
                        Disabled inputs are hidden from estimator and department pricing forms.
                    </p>
                </DropdownSection>

                <div>
                    <p className="text-sm font-semibold text-gray-800 mb-2">Final Pricing</p>
                    <label className="rounded-2xl bg-gray-50 border border-gray-100 p-4 flex items-start gap-3 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={finalPricingRequired}
                            onChange={(e) => setFinalPricingRequired(e.target.checked)}
                            className="mt-1"
                        />
                        <div>
                            <p className="text-sm font-semibold text-zinc-800">
                                Require manual final pricing before quoting
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                                When on, a department can't be quoted to the customer until
                                staff enter a final price for it by hand. When off,
                                departments quote straight off the estimator's rough
                                pricing (or the designer's reviewed number, once entered) —
                                manual final pricing is still always available to
                                hand-adjust a specific department, it just isn't required.
                            </p>
                        </div>
                    </label>
                </div>

                <DropdownSection
                    title="Final pricing access"
                    subtext={`${finalPricingAccessCounts.view + finalPricingAccessCounts.edit} users have access`}
                    defaultOpen={false}
                    titleStyle="text-sm font-semibold"
                    subtextStyle="text-xs text-gray-500"
                    customMainStyle="!shadow-none !border-gray-100"
                    customClickableStyle="!px-4 !py-3"
                    customContainerStyle="!p-0 !bg-white"
                >
                    <div className="border-b border-gray-100 bg-gray-50 p-4">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <div className="rounded-2xl border border-gray-200 bg-white px-4 py-3">
                                <div className="flex items-center gap-2">
                                    <FiUserX className="text-gray-400" />

                                    <span className="text-xs font-semibold text-gray-500">
                                        No Access
                                    </span>
                                </div>

                                <p className="mt-2 text-xl font-semibold text-zinc-900">
                                    {finalPricingAccessCounts.none}
                                </p>
                            </div>

                            <div className="rounded-2xl border border-sky-100 bg-sky-50 px-4 py-3">
                                <div className="flex items-center gap-2">
                                    <FiEye className="text-sky-600" />

                                    <span className="text-xs font-semibold text-sky-700">
                                        View Only
                                    </span>
                                </div>

                                <p className="mt-2 text-xl font-semibold text-sky-900">
                                    {finalPricingAccessCounts.view}
                                </p>
                            </div>

                            <div className="rounded-2xl border border-green-100 bg-green-50 px-4 py-3">
                                <div className="flex items-center gap-2">
                                    <FiEdit3 className="text-green-600" />

                                    <span className="text-xs font-semibold text-green-700">
                                        View & Edit
                                    </span>
                                </div>

                                <p className="mt-2 text-xl font-semibold text-green-900">
                                    {finalPricingAccessCounts.edit}
                                </p>
                            </div>
                        </div>

                        <div className="relative mt-4">
                            <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />

                            <input
                                type="text"
                                value={finalPricingUserSearch}
                                onChange={event =>
                                    setFinalPricingUserSearch(event.target.value)
                                }
                                placeholder="Search users by name, email or role..."
                                className="w-full rounded-2xl border border-gray-200 bg-white py-3 pl-10 pr-4 text-sm text-zinc-800 outline-none transition focus:border-zinc-400 focus:ring-2 focus:ring-zinc-900/5"
                            />
                        </div>

                        <p className="mt-3 text-xs text-gray-500">
                            View Only grants{" "}
                            <code className="font-semibold">
                                view_finalpricing_page
                            </code>
                            . View & Edit grants both final pricing permissions.
                        </p>
                    </div>

                    <div className="max-h-[520px] overflow-y-auto divide-y divide-gray-100">
                        {filteredFinalPricingUsers.length === 0 ? (
                            <div className="p-6 text-center">
                                <p className="text-sm font-semibold text-gray-500">
                                    No matching users found.
                                </p>
                            </div>
                        ) : (
                            filteredFinalPricingUsers.map(user => {
                                const accessLevel =
                                    getFinalPricingAccessLevel(user);

                                const isSaving =
                                    savingFinalPricingUserIds.includes(user.id);

                                return (
                                    <div
                                        key={user.id}
                                        className="px-4 py-4 hover:bg-gray-50/70 transition"
                                    >
                                        <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
                                            <div className="flex items-center gap-3 min-w-0">
                                                <div className="w-11 h-11 rounded-full border border-gray-200 bg-gray-100 overflow-hidden shrink-0">
                                                    {user.avatar_url ? (
                                                        <img
                                                            src={user.avatar_url}
                                                            alt=""
                                                            className="w-full h-full object-cover"
                                                        />
                                                    ) : (
                                                        <div className="w-full h-full flex items-center justify-center text-sm font-semibold text-gray-500">
                                                            {String(
                                                                user.full_name ||
                                                                user.username ||
                                                                user.email ||
                                                                "U"
                                                            )
                                                                .charAt(0)
                                                                .toUpperCase()}
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="min-w-0">
                                                    <div className="flex flex-wrap items-center gap-2">
                                                        <p className="text-sm font-semibold text-zinc-900 truncate">
                                                            {user.full_name ||
                                                                user.username ||
                                                                "Unnamed User"}
                                                        </p>

                                                        {isSaving && (
                                                            <span className="rounded-full bg-amber-50 px-2 py-1 text-[10px] font-semibold text-amber-700">
                                                                Saving...
                                                            </span>
                                                        )}
                                                    </div>

                                                    <p className="mt-0.5 text-xs text-gray-500 truncate">
                                                        {user.email}
                                                    </p>

                                                    <div className="mt-1.5 flex flex-wrap items-center gap-2">
                                                        <span className="rounded-full bg-gray-100 px-2 py-1 text-[10px] font-semibold text-gray-500 capitalize">
                                                            {String(user.role || "user")
                                                                .replaceAll("_", " ")}
                                                        </span>

                                                        {user.position && (
                                                            <span className="text-[10px] text-gray-400">
                                                                {user.position}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 xl:w-[520px]">
                                                {FINAL_PRICING_ACCESS_OPTIONS.map(
                                                    option => {
                                                        const selected =
                                                            accessLevel === option.value;

                                                        const Icon = option.icon;

                                                        const selectedClass =
                                                            option.value === "edit"
                                                                ? "border-green-600 bg-green-600 text-white"
                                                                : option.value === "view"
                                                                    ? "border-sky-600 bg-sky-600 text-white"
                                                                    : "border-zinc-700 bg-zinc-700 text-white";

                                                        return (
                                                            <button
                                                                key={`${user.id}-${option.value}`}
                                                                type="button"
                                                                disabled={isSaving}
                                                                onClick={() =>
                                                                    updateFinalPricingUserAccess(
                                                                        user,
                                                                        option.value
                                                                    )
                                                                }
                                                                title={option.description}
                                                                className={`flex items-center justify-center gap-2 rounded-xl border px-3 py-2.5 text-xs font-semibold transition disabled:cursor-wait disabled:opacity-60 ${selected
                                                                        ? selectedClass
                                                                        : "border-gray-200 bg-white text-gray-500 hover:border-gray-300 hover:bg-gray-50"
                                                                    }`}
                                                            >
                                                                <Icon className="shrink-0" />

                                                                {option.label}
                                                            </button>
                                                        );
                                                    }
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </DropdownSection>

                <DropdownSection
                    title="Quotation access"
                    subtext={`${quotationAccessCounts.view + quotationAccessCounts.send} users have access`}
                    defaultOpen={false}
                    titleStyle="text-sm font-semibold"
                    subtextStyle="text-xs text-gray-500"
                    customMainStyle="!shadow-none !border-gray-100"
                    customClickableStyle="!px-4 !py-3"
                    customContainerStyle="!p-0 !bg-white"
                >
                    <div className="border-b border-gray-100 bg-gray-50 p-4">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <div className="rounded-2xl border border-gray-200 bg-white px-4 py-3">
                                <div className="flex items-center gap-2">
                                    <FiUserX className="text-gray-400" />
                                    <span className="text-xs font-semibold text-gray-500">No Access</span>
                                </div>
                                <p className="mt-2 text-xl font-semibold text-zinc-900">
                                    {quotationAccessCounts.none}
                                </p>
                            </div>

                            <div className="rounded-2xl border border-sky-100 bg-sky-50 px-4 py-3">
                                <div className="flex items-center gap-2">
                                    <FiEye className="text-sky-600" />
                                    <span className="text-xs font-semibold text-sky-700">View Only</span>
                                </div>
                                <p className="mt-2 text-xl font-semibold text-sky-900">
                                    {quotationAccessCounts.view}
                                </p>
                            </div>

                            <div className="rounded-2xl border border-green-100 bg-green-50 px-4 py-3">
                                <div className="flex items-center gap-2">
                                    <FiSend className="text-green-600" />
                                    <span className="text-xs font-semibold text-green-700">View & Send</span>
                                </div>
                                <p className="mt-2 text-xl font-semibold text-green-900">
                                    {quotationAccessCounts.send}
                                </p>
                            </div>
                        </div>

                        <div className="relative mt-4">
                            <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                value={quotationUserSearch}
                                onChange={event => setQuotationUserSearch(event.target.value)}
                                placeholder="Search users by name, email or role..."
                                className="w-full rounded-2xl border border-gray-200 bg-white py-3 pl-10 pr-4 text-sm text-zinc-800 outline-none transition focus:border-zinc-400 focus:ring-2 focus:ring-zinc-900/5"
                            />
                        </div>

                        <p className="mt-3 text-xs text-gray-500">
                            View Only grants{" "}
                            <code className="font-semibold">view_quotation_page</code>
                            . View & Send grants both quotation permissions.
                        </p>
                    </div>

                    <div className="max-h-[520px] overflow-y-auto divide-y divide-gray-100">
                        {filteredQuotationUsers.length === 0 ? (
                            <div className="p-6 text-center">
                                <p className="text-sm font-semibold text-gray-500">
                                    No matching users found.
                                </p>
                            </div>
                        ) : (
                            filteredQuotationUsers.map(user => {
                                const accessLevel = getQuotationAccessLevel(user);
                                const isSaving = savingQuotationUserIds.includes(user.id);

                                return (
                                    <div
                                        key={user.id}
                                        className="px-4 py-4 hover:bg-gray-50/70 transition"
                                    >
                                        <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
                                            <div className="flex items-center gap-3 min-w-0">
                                                <div className="w-11 h-11 rounded-full border border-gray-200 bg-gray-100 overflow-hidden shrink-0">
                                                    {user.avatar_url ? (
                                                        <img
                                                            src={user.avatar_url}
                                                            alt=""
                                                            className="w-full h-full object-cover"
                                                        />
                                                    ) : (
                                                        <div className="w-full h-full flex items-center justify-center text-sm font-semibold text-gray-500">
                                                            {String(
                                                                user.full_name ||
                                                                user.username ||
                                                                user.email ||
                                                                "U"
                                                            )
                                                                .charAt(0)
                                                                .toUpperCase()}
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="min-w-0">
                                                    <div className="flex flex-wrap items-center gap-2">
                                                        <p className="text-sm font-semibold text-zinc-900 truncate">
                                                            {user.full_name ||
                                                                user.username ||
                                                                "Unnamed User"}
                                                        </p>

                                                        {isSaving && (
                                                            <span className="rounded-full bg-amber-50 px-2 py-1 text-[10px] font-semibold text-amber-700">
                                                                Saving...
                                                            </span>
                                                        )}
                                                    </div>

                                                    <p className="mt-0.5 text-xs text-gray-500 truncate">
                                                        {user.email}
                                                    </p>

                                                    <div className="mt-1.5 flex flex-wrap items-center gap-2">
                                                        <span className="rounded-full bg-gray-100 px-2 py-1 text-[10px] font-semibold text-gray-500 capitalize">
                                                            {String(user.role || "user")
                                                                .replaceAll("_", " ")}
                                                        </span>

                                                        {user.position && (
                                                            <span className="text-[10px] text-gray-400">
                                                                {user.position}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 xl:w-[520px]">
                                                {QUOTATION_ACCESS_OPTIONS.map(
                                                    option => {
                                                        const selected =
                                                            accessLevel === option.value;

                                                        const Icon = option.icon;

                                                        const selectedClass =
                                                            option.value === "send"
                                                                ? "border-green-600 bg-green-600 text-white"
                                                                : option.value === "view"
                                                                    ? "border-sky-600 bg-sky-600 text-white"
                                                                    : "border-zinc-700 bg-zinc-700 text-white";

                                                        return (
                                                            <button
                                                                key={`${user.id}-${option.value}`}
                                                                type="button"
                                                                disabled={isSaving}
                                                                onClick={() =>
                                                                    updateQuotationUserAccess(
                                                                        user,
                                                                        option.value
                                                                    )
                                                                }
                                                                title={option.description}
                                                                className={`flex items-center justify-center gap-2 rounded-xl border px-3 py-2.5 text-xs font-semibold transition disabled:cursor-wait disabled:opacity-60 ${selected
                                                                        ? selectedClass
                                                                        : "border-gray-200 bg-white text-gray-500 hover:border-gray-300 hover:bg-gray-50"
                                                                    }`}
                                                            >
                                                                <Icon className="shrink-0" />

                                                                {option.label}
                                                            </button>
                                                        );
                                                    }
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </DropdownSection>

                <div>
                    <p className="text-sm font-semibold text-gray-800 mb-2">Pricing Entered Notification</p>
                    <p className="text-xs text-gray-500 mb-2">
                        Notifies selected users (push + email) when all required pricing fields have been filled for a job.
                    </p>

                    <div className="space-y-3">
                        <label className="rounded-2xl bg-gray-50 border border-gray-100 p-4 flex items-start gap-3 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={!!pricingSettings.send_email_on_estimation_complete}
                                onChange={(e) =>
                                    setPricingEnteredNotifyEnabled(e.target.checked)
                                }
                                className="mt-1"
                            />
                            <div>
                                <p className="text-sm font-semibold text-zinc-800">
                                    Send notification when pricing is entered
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                    Sends a notification to selected recipients after estimation is completed.
                                </p>
                            </div>
                        </label>

                        <DropdownSection
                            title="Recipients"
                            subtext={`${pricingSettings.estimation_complete_email_user_ids?.length || 0} selected`}
                            defaultOpen={false}
                            titleStyle="text-sm font-semibold"
                            subtextStyle="text-xs text-gray-500"
                            customMainStyle="!shadow-none !border-gray-100"
                            customClickableStyle="!px-4 !py-3"
                            customContainerStyle="!p-0 !bg-white"
                        >
                            <div className="rounded-2xl bg-white border border-gray-100 divide-y divide-gray-100 max-h-72 overflow-auto">
                                {companyUsers.map(user => {
                                    const selected = pricingSettings.estimation_complete_email_user_ids?.includes(user.id);

                                    return (
                                        <button
                                            key={user.id}
                                            type="button"
                                            onClick={() => togglePricingEmailUser(user.id)}
                                            className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-gray-50"
                                        >
                                            <div className="min-w-0">
                                                <p className="text-sm font-semibold text-zinc-800 truncate">
                                                    {user.full_name || user.username || user.email}
                                                </p>
                                                <p className="text-xs text-gray-500 truncate">
                                                    {user.email}
                                                </p>
                                            </div>

                                            <span className={`rounded-full px-2 py-1 text-[11px] font-semibold shrink-0 ${selected
                                                ? "bg-zinc-900 text-white"
                                                : "bg-gray-100 text-gray-400"
                                                }`}>
                                                {selected ? "Selected" : "Select"}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        </DropdownSection>
                    </div>
                </div>

                {/* Always rendered: FinalPricingDetails.js's manual final-price
                    form is visible for every company now, and its save() fires
                    notifyReadyForQuotation on first entry gated only on the
                    notify_final_pricing_entered flag below — never on
                    final_pricing_required_for_quotation. So these settings must
                    stay reachable whatever that toggle says. */}
                <div>
                    <p className="text-sm font-semibold text-gray-800 mb-2">Ready for Quotation Notification</p>
                    <p className="text-xs text-gray-500 mb-2">
                        Notifies selected users (push + email) each time a department's final price is entered.
                    </p>

                    <div className="space-y-3">
                        <label className="rounded-2xl bg-gray-50 border border-gray-100 p-4 flex items-start gap-3 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={!!pricingSettings.notify_final_pricing_entered}
                                onChange={(e) =>
                                    setReadyForQuotationNotifyEnabled(e.target.checked)
                                }
                                className="mt-1"
                            />
                            <div>
                                <p className="text-sm font-semibold text-zinc-800">
                                    Send notification when a department's final price is entered
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                    Sends a notification to selected recipients each time a department final price is entered.
                                </p>
                            </div>
                        </label>

                        <DropdownSection
                            title="Recipients"
                            subtext={`${pricingSettings.final_pricing_entered_notify_user_ids?.length || 0} selected`}
                            defaultOpen={false}
                            titleStyle="text-sm font-semibold"
                            subtextStyle="text-xs text-gray-500"
                            customMainStyle="!shadow-none !border-gray-100"
                            customClickableStyle="!px-4 !py-3"
                            customContainerStyle="!p-0 !bg-white"
                        >
                            <div className="rounded-2xl bg-white border border-gray-100 divide-y divide-gray-100 max-h-72 overflow-auto">
                                {companyUsers.map(user => {
                                    const selected = pricingSettings.final_pricing_entered_notify_user_ids?.includes(user.id);

                                    return (
                                        <button
                                            key={user.id}
                                            type="button"
                                            onClick={() => toggleReadyForQuotationNotifyUser(user.id)}
                                            className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-gray-50"
                                        >
                                            <div className="min-w-0">
                                                <p className="text-sm font-semibold text-zinc-800 truncate">
                                                    {user.full_name || user.username || user.email}
                                                </p>
                                                <p className="text-xs text-gray-500 truncate">
                                                    {user.email}
                                                </p>
                                            </div>

                                            <span className={`rounded-full px-2 py-1 text-[11px] font-semibold shrink-0 ${selected
                                                ? "bg-zinc-900 text-white"
                                                : "bg-gray-100 text-gray-400"
                                                }`}>
                                                {selected ? "Selected" : "Select"}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        </DropdownSection>
                    </div>
                </div>
            </div>
        </SectionCard>
    );
};

export default PricingQuotationSettings;
